import { useTranslation } from 'next-i18next'

import { HorizontalNavItemType } from '~/types/layout'
import { selectChatNotification } from '~/redux/slices/chat'
import { useAppSelector } from '~/redux/hooks'
import { selectHighlightedItem } from '~/redux/slices/dashboard'
import checklistStyles from '~/checklist/styles/Checklist.module.css'
import { HighlightIds } from '~/checklist/constants'
import { useGetBadgeGradient } from '~/hooks/useGetLeagueInfo'
import { LeagueType } from '~/Leaderboard/types/leaderboard.types'
import ChatAiOpenButton from '~/aioracle/ChatAiOpenButton'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { Link } from '~/components/Link'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '32px' }]

export const HorizontalNavItem = ({
  label,
  onClick,
  className,
  href,
  iconSrc,
  itemKey,
  iconHeight,
  currentLeague,
  leagueRank,
  iconWidth,
  show,
}: HorizontalNavItemType): React.ReactElement => {
  const chatNotification = useAppSelector(selectChatNotification)
  const chatNotificationCx = getChatNotificationCx(itemKey, chatNotification)
  const iconClassNames = className?.icon || ''
  const leagueGradientClass = useGetBadgeGradient(currentLeague)
  const shadowStyle = { filter: itemKey === 'league' ? 'drop-shadow(0px 1.24px 1.24px rgba(0, 0, 0, 0.13))' : 'none' }

  const { t } = useTranslation('imageAlt')

  const highlightedItem = useAppSelector(selectHighlightedItem)
  const wrapperClasses = `flex flex-col items-center justify-center p-1 no-tap-color ${
    itemKey !== 'globe-ai' ? 'bg-white mx-1 mt-3 border-1.5 border-solid border-purple-dusk' : 'border-0'
  } rounded-3 w-17 h-13.5 ${className?.wrapper || ''}`
  const highlightClass =
    highlightedItem === HighlightIds.EXPLORE_LEADERBOARD && itemKey === 'leaderboard'
      ? checklistStyles['highlight']
      : ''

  const highlightId =
    highlightedItem === HighlightIds.EXPLORE_LEADERBOARD && itemKey === 'leaderboard'
      ? HighlightIds.EXPLORE_LEADERBOARD
      : ''
  if (!show) return <></>
  const content = (
    <div
      className={`flex flex-col justify-between items-center h-full mt-0.5 ${highlightClass}`}
      id={highlightId}
      style={shadowStyle}
    >
      <ResponsiveImage
        alt={t(`icons.${label.toLowerCase()}`)}
        height={iconHeight || 32}
        id={highlightId}
        imageClassName={`${
          itemKey === 'curriculum' ? 'w-6.5' : itemKey === 'globe-ai' ? 'w-10' : 'w-8'
        } ${iconClassNames} ${chatNotificationCx}`}
        imageWidths={`${iconWidth || 32}`}
        mediaQuery={mediaQuery}
        srcKey={iconSrc || ''}
        width={iconWidth || 32}
      />
      {currentLeague !== undefined && currentLeague !== 0 && (
        <div className="flex fixed transform text-white will-change-transform">
          {leagueRank ? (
            <>
              <span className={`mt-1.5 -ml-0.5 text-xxs  font-semibold text-add-gradient ${leagueGradientClass}`}>
                #
              </span>
              <span className={`mt-1 text-xs font-bold text-add-gradient ${leagueGradientClass}`}>{leagueRank}</span>
            </>
          ) : (
            <ResponsiveImage
              height={35}
              imageClassName="inline-block w-2 md:w-2 h-4 md:h-4 lg:h-7 lg:w-3.5"
              srcKey={`gamification/badges/league/zero-rank-${LeagueType[currentLeague].toLowerCase()}.png`}
              width={18}
            />
          )}
        </div>
      )}
      <div
        className={`text-gray-light-fade font-mediumn leading-none  text-2.5 sm:text-xxs text-center ${
          className?.label || ''
        }`}
      >
        {label}
      </div>
    </div>
  )

  if (href) {
    return (
      <Link className={wrapperClasses} to={href}>
        {content}
      </Link>
    )
  } else if (itemKey === 'globe-ai') {
    return (
      <div
        className={`relative ${wrapperClasses} ${highlightClass}`}
        id={highlightId}
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex={0}
      >
        {content}
        <ChatAiOpenButton isMobile />
      </div>
    )
  } else {
    return (
      <button className={`${wrapperClasses} ${highlightClass}`} id={highlightId} onClick={onClick} onKeyDown={onClick}>
        {content}
      </button>
    )
  }
}

function getChatNotificationCx(key: string, hasNotification: boolean): string {
  if (key === 'chat' && hasNotification) {
    return 'animate-scale'
  } else {
    return ''
  }
}
